<script setup lang="ts">
import { countries } from '~/assets/data/countries';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from 'zod';
import {
  ActivityCredibilityRatingsOptions,
  ActivityWhatDidYouLikeOptions,
  CommitmentToChangeOptions,
  TeacherRatingsOptions,
  validationOptions,
} from "~/enums/certificates/cme-outfitters.enums";
import { CourseCertificateStatus } from "~/enums/course.enums";

const { dispatchMessage } = useSystemMessages();
const { submitClaimCertificateForm, startDownload } = useCertificate();
const { course } = useCourseState();
const { user } = useUserStore();
const { t } = useI18n();

const loading = ref(false);

const emit = defineEmits<{
  (e: 'downloaded'): void;
}>();

const options = {
  yesOrNo: [
    t('global.dropdown.options.no'),
    t('global.dropdown.options.yes'),
  ],
  percentageOfNewInformation: [
    '25%',
    '50%',
    '75%',
    '100%',
  ],
};

// todo: refactor
const activityWhatDidYouLikeFields = [
  {
    value: ActivityWhatDidYouLikeOptions.IncreasedKnowledge,
    label: t('course.claimCertificate.form.credibility.fields.activityWhatDidYouLikeFields.labels.increasedKnowledge'),
  },
  {
    value: ActivityWhatDidYouLikeOptions.ImproveCommunicationWithHealthcareTeam,
    label: t('course.claimCertificate.form.credibility.fields.activityWhatDidYouLikeFields.labels.improveCommunicationWithHealthcareTeam'),
  },
  {
    value: ActivityWhatDidYouLikeOptions.InformationToReviewLater,
    label: t('course.claimCertificate.form.credibility.fields.activityWhatDidYouLikeFields.labels.informationToReviewLater'),
  },
  {
    value: ActivityWhatDidYouLikeOptions.ApplyInformationOnPractice,
    label: t('course.claimCertificate.form.credibility.fields.activityWhatDidYouLikeFields.labels.applyInformationOnPractice'),
  },
  {
    value: ActivityWhatDidYouLikeOptions.FacilitatedValuesOrEthicsToInterprofessionalPractice,
    label: t('course.claimCertificate.form.credibility.fields.activityWhatDidYouLikeFields.labels.facilitatedValuesOrEthicsToInterprofessionalPractice'),
  },
];

// todo: refactor
const commitmentToChangeFields = [
  {
    value: CommitmentToChangeOptions.RegularlyAssessCOVID19Outpatients,
    label: t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.labels.regularlyAssessCovid19Outpatients'),
  },
  {
    value: CommitmentToChangeOptions.UseDrugInteractionCheckers,
    label: t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.labels.useDrugInteractionCheckers'),
  },
  {
    value: CommitmentToChangeOptions.ApplyEvidenceBasedGuidelines,
    label: t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.labels.applyEvidenceBasedGuidelines'),
  },
  {
    value: CommitmentToChangeOptions.EngageWithPharmacistsNursesAndInfectiousDiseaseSpecialists,
    label: t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.labels.engageWithPharmacistsNursesAndInfectiousDiseaseSpecialists'),
  },
  {
    value: CommitmentToChangeOptions.UseReferenceTools,
    label: t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.labels.useReferenceTools'),
  },
  {
    value: CommitmentToChangeOptions.NoPlanToMakeChanges,
    label: t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.labels.noPlanToMakeChanges'),
  },
  {
    value: CommitmentToChangeOptions.Other,
    label: t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.labels.other'),
  },
];

// todo: refactor
const teacherRatingsFields = [
  {
    title: (name: string) => t('course.claimCertificate.form.faculty.fields.teacherRatings.labels.teachingStrategies', { name }),
    value: TeacherRatingsOptions.TeachingStrategies,
  },
  {
    title: (name: string) => t('course.claimCertificate.form.faculty.fields.teacherRatings.labels.expertise', { name }),
    value: TeacherRatingsOptions.Expertise,
  },
  {
    title: (name: string) => t('course.claimCertificate.form.faculty.fields.teacherRatings.labels.engaging', { name }),
    value: TeacherRatingsOptions.Engaging,
  },
  {
    title: (name: string) => t('course.claimCertificate.form.faculty.fields.teacherRatings.labels.contentSelection', { name }),
    value: TeacherRatingsOptions.ContentSelection,
  },
];

// todo: refactor
const activityCredibilityRatingsFields = [
  {
    title: t('course.claimCertificate.form.credibility.fields.activityCredibilityRatings.labels.changePracticeBasedOnActivity'),
    options: options.yesOrNo,
    value: ActivityCredibilityRatingsOptions.ChangePracticeBasedOnActivity,
  },
  {
    title: t('course.claimCertificate.form.credibility.fields.activityCredibilityRatings.labels.clinicallyRelevant'),
    options: options.yesOrNo,
    value: ActivityCredibilityRatingsOptions.ClinicallyRelevant,
  },
  {
    title: t('course.claimCertificate.form.credibility.fields.activityCredibilityRatings.labels.balancedAndObjective'),
    options: options.yesOrNo,
    value: ActivityCredibilityRatingsOptions.BalancedAndObjective,
  },
  {
    title: t('course.claimCertificate.form.credibility.fields.activityCredibilityRatings.labels.freeOfCommercialBias'),
    options: options.yesOrNo,
    value: ActivityCredibilityRatingsOptions.FreeOfCommercialBias,
  },
  {
    title: t('course.claimCertificate.form.credibility.fields.activityCredibilityRatings.labels.percentageOfNewInformation'),
    options: options.percentageOfNewInformation,
    value: ActivityCredibilityRatingsOptions.PercentageOfNewInformation,
  },
];

/*
 * schema
 */
const schema = toTypedSchema(
  zod.object({

    /**
     * PERSONAL INFORMATION
     */
    firstName: zod.string({ message: t('general.error.validation.fieldIsRequired') }).min(1, t('general.error.validation.fieldIsRequired')),
    lastName: zod.string({ message: t('general.error.validation.fieldIsRequired') }).min(1, t('general.error.validation.fieldIsRequired')),

    userIsDoctorOfMedicine: zod.boolean({ message: t('general.error.validation.fieldIsRequired') }),

    country: zod.object({
      isoCode: zod.string({ message: t('general.error.validation.fieldIsRequired') })
        .refine(countryIsoCode => countries.map(country => country.code).includes(countryIsoCode), {
          message: t('general.error.validation.fieldIsRequired'),
        }),
      stateName: zod.string().optional(),
    }).refine(country => {
      if (country.isoCode === 'US' && (!country.stateName || country.stateName.trim() === '')) {
        return false;
      }
      return true;
    }, {
      message: t('course.claimCertificate.form.personalInformation.fields.state.validation.requiredForUS'),
      path: ['stateName'],
    }),

    /**
     * COMMITMENT TO CHANGE
     */
    commitmentToChange: zod.object({
      options: zod.object({
        [CommitmentToChangeOptions.RegularlyAssessCOVID19Outpatients]: zod.boolean(),
        [CommitmentToChangeOptions.UseDrugInteractionCheckers]: zod.boolean(),
        [CommitmentToChangeOptions.ApplyEvidenceBasedGuidelines]: zod.boolean(),
        [CommitmentToChangeOptions.EngageWithPharmacistsNursesAndInfectiousDiseaseSpecialists]: zod.boolean(),
        [CommitmentToChangeOptions.UseReferenceTools]: zod.boolean(),
        [CommitmentToChangeOptions.NoPlanToMakeChanges]: zod.boolean(),
        [CommitmentToChangeOptions.Other]: zod.boolean(),
      }).refine(commitmentToChangeOptions => {
        return Object.values(commitmentToChangeOptions).some(value => value === true);
      },
      { message:  t('general.error.validation.atLeastOneOptionMustBeSelected') },
      ),
      otherText: zod.string().optional(),
    }).refine(commitmentToChange => {
      if (commitmentToChange.options[CommitmentToChangeOptions.Other] && commitmentToChange.otherText?.trim() === '') {
        return false;
      }
      return true;
    }, {
      message: t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.validation.fieldsRequiredIfOtherSelected'),
      path: ['otherText'],
    }),

    /**
     * OBJECTIVES
     */
    learningGoalsRatings: zod.array(
      zod.object({
        title: zod.string().min(1),
        rating: zod.number()
          .min(1, t('course.claimCertificate.form.rating.validation.min'))
          .max(5, t('course.claimCertificate.form.rating.validation.max')),
      }),
    ),

    /**
     * FACULTY
     */
    teacherRatings: zod.array(
      zod.object({
        name: zod.string().min(1),
        [TeacherRatingsOptions.TeachingStrategies]: zod.number()
          .min(1, t('course.claimCertificate.form.rating.validation.min'))
          .max(5, t('course.claimCertificate.form.rating.validation.max')),
        [TeacherRatingsOptions.Expertise]: zod.number()
          .min(1, t('course.claimCertificate.form.rating.validation.min'))
          .max(5, t('course.claimCertificate.form.rating.validation.max')),
        [TeacherRatingsOptions.Engaging]: zod.number()
          .min(1, t('course.claimCertificate.form.rating.validation.min'))
          .max(5, t('course.claimCertificate.form.rating.validation.max')),
        [TeacherRatingsOptions.ContentSelection]: zod.number()
          .min(1, t('course.claimCertificate.form.rating.validation.min'))
          .max(5, t('course.claimCertificate.form.rating.validation.max')),
      }),
    ),

    /**
     * CREDIBILITY
     */
    activityCredibilityRatings: zod.object({
      [ActivityCredibilityRatingsOptions.ChangePracticeBasedOnActivity]: zod.string()
        .refine(val => options.yesOrNo.includes(val), {
          message: t('general.error.validation.invalidValue'),
        }),
      [ActivityCredibilityRatingsOptions.ClinicallyRelevant]: zod.string()
        .refine(val => options.yesOrNo.includes(val), {
          message: t('general.error.validation.invalidValue'),
        }),
      [ActivityCredibilityRatingsOptions.BalancedAndObjective]: zod.string()
        .refine(val => options.yesOrNo.includes(val), {
          message: t('general.error.validation.invalidValue'),
        }),
      [ActivityCredibilityRatingsOptions.FreeOfCommercialBias]: zod.string()
        .refine(val => options.yesOrNo.includes(val), {
          message: t('general.error.validation.invalidValue'),
        }),
      [ActivityCredibilityRatingsOptions.PercentageOfNewInformation]: zod.string()
        .refine(val => options.percentageOfNewInformation.includes(val), {
          message: t('general.error.validation.invalidValue'),
        }),
    }),

    activityWhatDidYouLike: zod.object({
      [ActivityWhatDidYouLikeOptions.IncreasedKnowledge]: zod.boolean(),
      [ActivityWhatDidYouLikeOptions.ImproveCommunicationWithHealthcareTeam]: zod.boolean(),
      [ActivityWhatDidYouLikeOptions.InformationToReviewLater]: zod.boolean(),
      [ActivityWhatDidYouLikeOptions.ApplyInformationOnPractice]: zod.boolean(),
      [ActivityWhatDidYouLikeOptions.FacilitatedValuesOrEthicsToInterprofessionalPractice]: zod.boolean(),
    }).refine(data =>
      Object.values(data).some(value => value === true),
    { message: t('general.error.validation.atLeastOneOptionMustBeSelected') },
    ),

    activityMostUsefulInformation: zod.string({ message: t('general.error.validation.fieldIsRequired') }).min(1, t('general.error.validation.fieldIsRequired')),

    /**
     * GENERAL
     */
    suggestedTopicsForFuture: zod.string({ message: t('general.error.validation.fieldIsRequired') }).min(1, t('general.error.validation.fieldIsRequired')),
    generalCommentsAndSuggestions: zod.string({ message: t('general.error.validation.fieldIsRequired') }).min(1, t('general.error.validation.fieldIsRequired')),

    userAgreedToDataSharing: zod.boolean({ message: t('general.error.validation.fieldIsRequired') })
      .refine(data => data,
        { message: t('general.error.validation.fieldIsRequired') },
      ),

    courseRating: zod.number({ message: t('general.error.validation.fieldIsRequired') })
      .min(1, t('course.claimCertificate.form.rating.validation.min'))
      .max(5, t('course.claimCertificate.form.rating.validation.max')),
  }),
);

const { defineField, errors, handleSubmit } = useForm({
  validationSchema: schema,
  initialValues: {
    firstName: user.value?.firstName || '',
    lastName: user.value?.lastName || '',

    country: {
      isoCode: '',
    },

    learningGoalsRatings: course.value?.learningGoals.map(goal => ({
      title: goal,
      rating: 0,
    })),

    teacherRatings: course.value?.teachers.map(teacher => ({
      name: teacher.name,
      [TeacherRatingsOptions.TeachingStrategies]: 0,
      [TeacherRatingsOptions.Expertise]: 0,
      [TeacherRatingsOptions.Engaging]: 0,
      [TeacherRatingsOptions.ContentSelection]: 0,
    })),

    activityCredibilityRatings: {
      [ActivityCredibilityRatingsOptions.ChangePracticeBasedOnActivity]: '',
      [ActivityCredibilityRatingsOptions.ClinicallyRelevant]: '',
      [ActivityCredibilityRatingsOptions.BalancedAndObjective]: '',
      [ActivityCredibilityRatingsOptions.FreeOfCommercialBias]: '',
      [ActivityCredibilityRatingsOptions.PercentageOfNewInformation]: '',
    },

    activityWhatDidYouLike: {
      [ActivityWhatDidYouLikeOptions.IncreasedKnowledge]: false,
      [ActivityWhatDidYouLikeOptions.ImproveCommunicationWithHealthcareTeam]: false,
      [ActivityWhatDidYouLikeOptions.InformationToReviewLater]: false,
      [ActivityWhatDidYouLikeOptions.ApplyInformationOnPractice]: false,
      [ActivityWhatDidYouLikeOptions.FacilitatedValuesOrEthicsToInterprofessionalPractice]: false,
    },

    commitmentToChange: {
      options: {
        [CommitmentToChangeOptions.RegularlyAssessCOVID19Outpatients]: false,
        [CommitmentToChangeOptions.UseDrugInteractionCheckers]: false,
        [CommitmentToChangeOptions.ApplyEvidenceBasedGuidelines]: false,
        [CommitmentToChangeOptions.EngageWithPharmacistsNursesAndInfectiousDiseaseSpecialists]: false,
        [CommitmentToChangeOptions.UseReferenceTools]: false,
        [CommitmentToChangeOptions.NoPlanToMakeChanges]: false,
        [CommitmentToChangeOptions.Other]: false,
      },
      otherText: '',
    },
  },
});

/**
 * PERSONAL INFORMATION
 */
const [firstName, firstNameProps] = defineField("firstName", validationOptions);
const [lastName, lastNameProps] = defineField("lastName", validationOptions);

const [userIsDoctorOfMedicine, userIsDoctorOfMedicineProps] = defineField("userIsDoctorOfMedicine", validationOptions);

const [countryIsoCode, countryIsoCodeProps] = defineField("country.isoCode", validationOptions);
const [countryStateName, countryStateNameProps] = defineField("country.stateName", validationOptions);

/**
 * COMMITMENT TO CHANGE
 */
const [commitmentToChangeOptions, commitmentToChangeOptionsProps] = defineField("commitmentToChange.options", validationOptions);
const [commitmentToChangeOtherText, commitmentToChangeOtherTextProps] = defineField("commitmentToChange.otherText", validationOptions);

/**
 * OBJECTIVES
 */
const [learningGoalsRatings] = defineField('learningGoalsRatings', validationOptions);

/**
 * FACULTY
 */
const [teacherRatings] = defineField('teacherRatings', validationOptions);

/**
 * CREDIBILITY
 */
const [activityCredibilityRatings] = defineField('activityCredibilityRatings', validationOptions);
const [activityWhatDidYouLike, activityWhatDidYouLikeProps] = defineField("activityWhatDidYouLike", validationOptions);
const [activityMostUsefulInformation, activityMostUsefulInformationProps] = defineField("activityMostUsefulInformation", validationOptions);

/**
 * GENERAL
 */
const [suggestedTopicsForFuture, suggestedTopicsForFutureProps] = defineField("suggestedTopicsForFuture", validationOptions);
const [generalCommentsAndSuggestions, generalCommentsAndSuggestionsProps] = defineField("generalCommentsAndSuggestions", validationOptions);

const [userAgreedToDataSharing, userAgreedToDataSharingProps] = defineField("userAgreedToDataSharing", validationOptions);

const [courseRating, courseRatingProps] = defineField("courseRating", validationOptions);

/*
 * submit
 */
const onSubmit = handleSubmit(async values => {
  try {
    loading.value = true;

    if (!course.value?.id) {
      return;
    }

    const { country, commitmentToChange, ...restValues } = values;

    const selectedCommitments = Object.keys(commitmentToChange.options)
      .filter(key => commitmentToChange.options[key as CommitmentToChangeOptions])
      .map(key => {
        if (key === CommitmentToChangeOptions.Other && commitmentToChange.otherText) {
          return commitmentToChange.otherText;
        }

        const option = commitmentToChangeFields.find(option => option.value === key);
        return option ? option.label : null;
      });

    const body = {
      accreditationBody: 'accme',
      certificateType: 'cme_certificate',
      commitmentToChange: selectedCommitments,
      countryIsoCode: country.isoCode,
      countryStateName: country.stateName,
      ...restValues,

      // filter selected statements and merge them into an array of strings
      activityWhatDidYouLike: activityWhatDidYouLikeFields.filter(field =>
        Object.keys(restValues.activityWhatDidYouLike)
        // fixme: shit code
          .filter(key => key in restValues.activityWhatDidYouLike ? restValues.activityWhatDidYouLike[key as
              ActivityWhatDidYouLikeOptions.IncreasedKnowledge ||
              ActivityWhatDidYouLikeOptions.ImproveCommunicationWithHealthcareTeam ||
              ActivityWhatDidYouLikeOptions.InformationToReviewLater ||
              ActivityWhatDidYouLikeOptions.ApplyInformationOnPractice ||
              ActivityWhatDidYouLikeOptions.FacilitatedValuesOrEthicsToInterprofessionalPractice
          ] : false)
          .includes(field.value),
      ).map(item => item.label),
    };

    const response = await submitClaimCertificateForm(course.value.id, body);

    startDownload(response.data.value as BlobPart, `${t('course.claimCertificate.form.submit.filename')} - ${course.value?.courseTitle}.pdf`);
    dispatchMessage(t('course.claimCertificate.form.submit.success.title'), 'success', t('course.claimCertificate.form.submit.success.message'));
    if (course.value){
      course.value.certificateStatus = CourseCertificateStatus.CmeClaimed;
    }
    emit('downloaded');

  } catch (error) {
    interface ClaimCertificateError {
      data: {
        message: string;
      };
    }

    dispatchMessage(t('global.error.default.title'), 'error', (error as ClaimCertificateError)?.data?.message || t('global.error.default.message'));

  } finally {
    loading.value = false;
  }
});

const handleCommitmentSelection = (selectedCommitment: CommitmentToChangeOptions) => {
  const valueOfCommitmentToChange = commitmentToChangeOptions.value ?? {};

  if (selectedCommitment && selectedCommitment === CommitmentToChangeOptions.NoPlanToMakeChanges) {
    Object.keys(valueOfCommitmentToChange).forEach(key => {
      if (key !== CommitmentToChangeOptions.NoPlanToMakeChanges) {
        valueOfCommitmentToChange[key as CommitmentToChangeOptions] = false;
      }
    });
  }

  if (valueOfCommitmentToChange && selectedCommitment !== CommitmentToChangeOptions.NoPlanToMakeChanges) {
    valueOfCommitmentToChange[CommitmentToChangeOptions.NoPlanToMakeChanges] = false;
  }

  if (!valueOfCommitmentToChange[CommitmentToChangeOptions.Other]) {
    commitmentToChangeOtherText.value = '';
  }

  commitmentToChangeOptions.value = valueOfCommitmentToChange;
};

</script>

<template>
  <form
    class="flex flex-col gap-12"
    @submit="onSubmit"
  >
    <Card
      class="rounded-2xl p-0 text-neutral-90 shadow-medmastery"
      :pt="{
        body: {
          class: 'p-6'
        },
        content: {
          class: 'p-0'
        }
      }"
    >
      <template #content>
        <div
          v-sanitize-html="t('course.claimCertificate.submissionDisclaimer', {title: `<strong>${course?.courseTitle}</strong>`})"
          class="text-16 text-neutral-90 leading-150 font-normal"
        />
      </template>
    </Card>

    <!-- PROFESSIONAL INFORMATION -->
    <div class="flex flex-col gap-4">
      <Divider
        align="left"
        type="solid"
        class="m-0 mb-2"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{t('course.claimCertificate.form.personalInformation.title')}}</span>
      </Divider>

      <div class="grid grid-cols-2 gap-x-6">
        <div class="flex flex-col">
          <FormFieldLabel for="firstName">
            {{t('course.claimCertificate.form.personalInformation.fields.firstName.title')}}
          </FormFieldLabel>

          <InputText
            id="firstName"
            v-model="firstName"
            :invalid="!!errors.firstName"
            :disabled="loading"
            aria-describedby="firstName-help"
            @blur="firstNameProps.onBlur"
          />

          <FormFieldError id="firstName-help">
            {{errors.firstName}}
          </FormFieldError>
        </div>

        <div class="flex flex-col">
          <FormFieldLabel for="lastName">
            {{t('course.claimCertificate.form.personalInformation.fields.lastName.title')}}
          </FormFieldLabel>

          <InputText
            id="lastName"
            v-model="lastName"
            :invalid="!!errors.lastName"
            :disabled="loading"
            aria-describedby="lastName-help"
            @blur="lastNameProps.onBlur"
          />

          <FormFieldError id="lastName-help">
            {{errors.lastName}}
          </FormFieldError>
        </div>
      </div>

      <div class="flex flex-col">
        <FormFieldLabel for="userIsDoctorOfMedicine">
          {{t('course.claimCertificate.form.personalInformation.fields.doctorOfMedicine.title')}}
        </FormFieldLabel>

        <Dropdown
          id="userIsDoctorOfMedicine"
          v-model="userIsDoctorOfMedicine"
          placeholder="Please select"
          aria-describedby="userIsDoctorOfMedicine-help"
          option-label="label"
          option-value="value"
          :invalid="!!errors.userIsDoctorOfMedicine"
          :disabled="loading"
          :options="[
            {value: false, label: t('global.dropdown.options.no')},
            {value: true, label: t('global.dropdown.options.yes')}
          ]"
          @update:model-value="userIsDoctorOfMedicineProps.onBlur"
        />

        <FormFieldError id="userIsDoctorOfMedicine-help">
          {{errors.userIsDoctorOfMedicine}}
        </FormFieldError>
      </div>

      <div class="grid grid-cols-2 gap-x-6">
        <div class="flex flex-col">
          <FormFieldLabel for="countryIsoCode">
            {{t('course.claimCertificate.form.personalInformation.fields.country.title')}}
          </FormFieldLabel>

          <Dropdown
            id="countryIsoCode"
            v-model="countryIsoCode"
            placeholder="Please select"
            option-label="name"
            option-value="code"
            editable
            aria-describedby="countryIsoCode-help"
            :invalid="!!errors['country.isoCode']"
            :disabled="loading"
            :options="countries"
            :show-clear="!!countryIsoCode"
            append-to="self"
            @update:model-value="countryIsoCodeProps.onBlur"
          >
            <template
              v-if="countryIsoCode"
              #value="{value}"
            >
              <div class="flex items-center">
                <MIcon
                  sprite-location="/icons/country_flags.svg"
                  :icon="value"
                  class="rounded-sm mr-2 country-flag-icon--value"
                />

                <div>{{countries.find((item) => item.code === value)?.name}}</div>
              </div>
            </template>

            <template #option="{option}">
              <div class="flex items-center">
                <MIcon
                  sprite-location="/icons/country_flags.svg"
                  :icon="option.code"
                  class="rounded-sm mr-2 country-flag-icon--option"
                />

                <div class="text-wrap">
                  {{option.name}}
                </div>
              </div>
            </template>
          </Dropdown>

          <FormFieldError id="countryIsoCode-help">
            {{errors['country.isoCode']}}
          </FormFieldError>
        </div>

        <div class="flex flex-col">
          <FormFieldLabel for="countryStateName">
            {{t('course.claimCertificate.form.personalInformation.fields.state.title')}}
          </FormFieldLabel>

          <InputText
            id="countryStateName"
            v-model="countryStateName"
            :invalid="!!errors['country.stateName']"
            :disabled="loading"
            aria-describedby="countryStateName-help"
            @blur="countryStateNameProps.onBlur"
          />

          <FormFieldError id="countryStateName-help">
            {{errors['country.stateName']}}
          </FormFieldError>
        </div>
      </div>
    </div>

    <!-- COMMITMENT TO CHANGE -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{t('course.claimCertificate.form.commitmentToChange.title')}}</span>
      </Divider>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="commitmentToChangeOptions">
          {{t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOptions.title')}}
        </FormFieldLabel>

        <div class="flex flex-col gap-2">
          <label
            v-for="(option) in commitmentToChangeFields"
            :key="`commitmentToChangeOptions-${option.label}`"
            class="flex items-center gap-2"
          >
            <Checkbox
              v-if="commitmentToChangeOptions && option.value in commitmentToChangeOptions"
              v-model="commitmentToChangeOptions[option.value]"
              binary
              :invalid="!!errors['commitmentToChange.options']"
              @update:model-value="commitmentToChangeOptionsProps.onBlur"
              @change="handleCommitmentSelection(option.value)"
            />
            <span>
              {{option.label}}
            </span>
          </label>
        </div>

        <FormFieldError id="commitmentToChange-help">
          {{errors['commitmentToChange.options']}}
        </FormFieldError>

        <div
          v-if="commitmentToChangeOptions?.[CommitmentToChangeOptions.Other]"
          class="flex flex-col gap-2"
        >
          <InputText
            id="commitmentToChangeOtherText"
            v-model="commitmentToChangeOtherText"
            :placeholder="t('course.claimCertificate.form.commitmentToChange.fields.commitmentToChangeOtherText.title')"
            :disabled="loading"
            :invalid="!!errors['commitmentToChange.otherText']"
            aria-describedby="commitmentToChangeOtherText-help"
            @update:model-value="commitmentToChangeOtherTextProps.onBlur"
          />

          <FormFieldError id="commitmentToChangeOtherText-help">
            {{errors['commitmentToChange.otherText']}}
          </FormFieldError>
        </div>
      </div>
    </div>

    <!-- OBJECTIVES-->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{t('course.claimCertificate.form.objectives.title')}}</span>
      </Divider>

      <div class="text-18 font-bold text-neutral-90">
        {{t('course.claimCertificate.form.objectives.fields.learningGoalsRatings.title')}}
      </div>

      <div
        v-for="(learningGoal, index) in course?.learningGoals"
        :key="learningGoal"
      >
        <RatingScale
          v-if="learningGoalsRatings?.[index]"
          v-model="learningGoalsRatings[index].rating"
          :title="learningGoal"
          :label-left="t('course.claimCertificate.form.objectives.fields.learningGoalsRatings.options.stronglyDisagree')"
          :label-right="t('course.claimCertificate.form.objectives.fields.learningGoalsRatings.options.stronglyAgree')"
          :invalid="!!errors?.learningGoalsRatings && !learningGoalsRatings?.[index].rating"
        />
      </div>
    </div>

    <!-- FACULTY -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{t('course.claimCertificate.form.faculty.title')}}</span>
      </Divider>

      <template
        v-for="(teacher, index) in course?.teachers"
        :key="teacher.name"
      >
        <template
          v-for="(field) in teacherRatingsFields"
          :key="field"
        >
          <RatingScale
            v-if="teacherRatings?.[index]"
            v-model="teacherRatings[index][field.value]"
            :title="field.title(teacher.name)"
            :invalid="!!errors.teacherRatings && !teacherRatings[index][field.value]"
            :label-left="t('course.claimCertificate.form.faculty.fields.teacherRatings.options.poor')"
            :label-right="t('course.claimCertificate.form.faculty.fields.teacherRatings.options.excelent')"
          />
        </template>
      </template>
    </div>

    <!-- CREDIBILITY -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{t('course.claimCertificate.form.credibility.title')}}</span>
      </Divider>

      <template
        v-for="field in activityCredibilityRatingsFields"
        :key="field.title"
      >
        <RatingScale
          v-if="activityCredibilityRatings && field.value in activityCredibilityRatings"
          v-model="activityCredibilityRatings[field.value]"
          :title="field.title"
          :options="field.options"
          :invalid="!!errors.activityCredibilityRatings && !activityCredibilityRatings[field.value]"
        />
      </template>

      <div class="flex flex-col gap-4">
        <label class="text-16 font-bold text-neutral-80">
          {{t('course.claimCertificate.form.credibility.fields.activityWhatDidYouLikeFields.title')}}
        </label>

        <div class="flex flex-col gap-2">
          <label
            v-for="(item) in activityWhatDidYouLikeFields"
            :key="item.value"
            class="flex items-center gap-2"
          >
            <Checkbox
              v-if="activityWhatDidYouLike && item.value in activityWhatDidYouLike"
              v-model="activityWhatDidYouLike[item.value]"
              binary
              :invalid="!!errors.activityWhatDidYouLike"
              @update:model-value="activityWhatDidYouLikeProps.onBlur"
            />

            <span>
              {{item.label}}
            </span>
          </label>
        </div>

        <FormFieldError id="commitmentToChange-help">
          {{errors.activityWhatDidYouLike}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="activityMostUsefulInformation">
          {{t('course.claimCertificate.form.credibility.fields.activityMostUsefulInformation.title')}}
        </FormFieldLabel>

        <Textarea
          id="activityMostUsefulInformation"
          v-model="activityMostUsefulInformation"
          class="resize-y"
          :invalid="!!errors.activityMostUsefulInformation"
          :disabled="loading"
          aria-describedby="activityMostUsefulInformation-help"
          rows="5"
          @blur="activityMostUsefulInformationProps.onBlur"
        />

        <FormFieldError id="activityMostUsefulInformation-help">
          {{errors.activityMostUsefulInformation}}
        </FormFieldError>
      </div>
    </div>

    <!-- GENERAL -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{t('course.claimCertificate.form.general.title')}}</span>
      </Divider>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="activityMostUsefulInformation">
          {{t('course.claimCertificate.form.general.fields.suggestedTopicsForFuture.title')}}
        </FormFieldLabel>

        <Textarea
          id="suggestedTopicsForFuture"
          v-model="suggestedTopicsForFuture"
          :invalid="!!errors.suggestedTopicsForFuture"
          :disabled="loading"
          aria-describedby="suggestedTopicsForFuture-help"
          class="resize-y"
          rows="5"
          @blur="suggestedTopicsForFutureProps.onBlur"
        />

        <FormFieldError id="suggestedTopicsForFuture-help">
          {{errors.suggestedTopicsForFuture}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="activityMostUsefulInformation">
          {{t('course.claimCertificate.form.general.fields.generalCommentsAndSuggestions.title')}}
        </FormFieldLabel>

        <Textarea
          id="generalCommentsAndSuggestions"
          v-model="generalCommentsAndSuggestions"
          :invalid="!!errors.generalCommentsAndSuggestions"
          :disabled="loading"
          aria-describedby="generalCommentsAndSuggestions-help"
          class="resize-y"
          rows="5"
          @blur="generalCommentsAndSuggestionsProps.onBlur"
        />

        <FormFieldError id="generalCommentsAndSuggestions-help">
          {{errors.generalCommentsAndSuggestions}}
        </FormFieldError>
      </div>
    </div>

    <Divider class="m-0" />

    <!-- AGREEMENT -->
    <div class="flex flex-col gap-3">
      <div>
        <label class="flex gap-2 text-14 font-bold">
          <Checkbox
            v-model="userAgreedToDataSharing"
            binary
            :disabled="loading"
            @update:model-value="userAgreedToDataSharingProps.onBlur"
          />

          <span>
            {{t('course.claimCertificate.form.agreement.disclaimerOutfitters')}}
          </span>
        </label>
        <FormFieldError id="generalCommentsAndSuggestions-help">
          {{errors.userAgreedToDataSharing}}
        </FormFieldError>
      </div>

      <div class="text-neutral-70 text-14 leading-150">
        AMA PRA Category 1 Credits™: CME Outfitters, 201 N King of Prussia Rd, Suite 370, Radnor, PA 19087.
      </div>
    </div>

    <Divider class="m-0" />

    <div class="flex flex-col gap-4">
      <!-- COURSE RATING -->
      <div class="flex flex-col items-center gap-2">
        <div class="text-16">
          {{t('course.claimCertificate.form.rating.title')}}
        </div>
        <StarRating
          v-model="courseRating"
          :disabled="loading"
          @update:model-value="courseRatingProps.onBlur"
        />
        <FormFieldError id="generalCommentsAndSuggestions-help">
          {{errors.courseRating}}
        </FormFieldError>
      </div>

      <div class="flex justify-center">
        <Button
          :loading="loading"
          class="font-bold !text-22 flex gap-2 px-12 bg-blue-30 text-neutral-90 border-none rounded-full"
          type="submit"
          @click="onSubmit"
        >
          <FontAwesomeIcon
            v-if="loading"
            icon="fa-circle-notch"
            class="animate-spin"
          />
          <FontAwesomeIcon
            v-else
            icon="fa-file-certificate"
          />
          <span>{{t('course.claimCertificate.form.submit.label')}}</span>
        </Button>
      </div>
      <div class="flex justify-center mb-12">
        <FormFieldError
          v-if="Object.keys(errors).length > 0"
          id="form-error"
        >
          {{t('course.claimCertificate.form.submit.validationErrorMessage')}}
        </FormFieldError>
      </div>
    </div>
  </form>
</template>